import { useEffect, useState } from "react";
import { api, formatError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, KeyRound, UserX } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Employees() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [form, setForm] = useState({ name: "", email: "", mobile: "", role: "executive", division: "Retail", department: "Sales", designation: "" });
  const { user } = useAuth();
  const canAdmin = ["super_admin","partner"].includes(user?.role);

  const load = () => api.get("/users").then(r => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/roles").then(r => setRoles(r.data));
  }, []);

  const submit = async () => {
    try {
      const { data } = await api.post("/users", form);
      toast.success(`User created. Temp password: ${data.temp_password}`);
      setOpen(false); setForm({ name: "", email: "", mobile: "", role: "executive", division: "Retail", department: "Sales", designation: "" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active, login_enabled: !u.active }); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  const resetPw = async () => {
    try { await api.post(`/users/${pwUser.id}/reset-password`, { new_password: newPw }); toast.success("Password reset"); setPwOpen(false); setNewPw(""); }
    catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6 animate-in">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Team</div>
          <h1 className="font-serif-display text-3xl">Employees & Users</h1>
        </div>
        {canAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-user-btn"><Plus size={14} className="mr-1"/> Invite User</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Invite / Create User</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Full Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="user-name-input"/></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="user-email-input"/></div>
                <div><Label>Mobile</Label><Input value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })}/></div>
                <div><Label>Role</Label>
                  <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue/></SelectTrigger>
                    <SelectContent className="max-h-64 overflow-auto">
                      {roles.map(r => <SelectItem key={r.key} value={r.key}>{r.label || r.key}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Division</Label>
                  <Select value={form.division} onValueChange={v => setForm({ ...form, division: v })}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{["Mandate","Retail","Home Loan","-"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Department</Label><Input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })}/></div>
                <div><Label>Designation</Label><Input value={form.designation} onChange={e => setForm({ ...form, designation: e.target.value })}/></div>
              </div>
              <DialogFooter><Button onClick={submit} data-testid="user-submit-btn">Create User</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left px-4 py-3">Emp ID</th><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Division</th><th className="text-left px-4 py-3">Status</th>{canAdmin && <th className="text-right px-4 py-3">Actions</th>}</tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-t border-border hover:bg-accent/5" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-mono text-xs">{u.employee_id}</td>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3 capitalize">{(u.role || "").replace(/_/g," ")}</td>
                <td className="px-4 py-3">{u.division}</td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] px-2 py-0.5 rounded border ${u.active ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/20" : "bg-rose-500/10 text-rose-700 border-rose-500/20"}`}>{u.active ? "Active" : "Inactive"}</span>
                </td>
                {canAdmin && (
                  <td className="px-4 py-3 text-right space-x-2">
                    <Button variant="ghost" size="sm" onClick={() => { setPwUser(u); setPwOpen(true); }} data-testid={`reset-pw-${u.email}`}><KeyRound size={14} className="mr-1"/> Reset</Button>
                    <Button variant="ghost" size="sm" onClick={() => toggleActive(u)} data-testid={`toggle-${u.email}`}><UserX size={14} className="mr-1"/>{u.active ? "Deactivate" : "Activate"}</Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password for {pwUser?.name}</DialogTitle></DialogHeader>
          <div><Label>New Password</Label><Input type="text" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Min 6 chars" data-testid="reset-pw-input"/></div>
          <DialogFooter><Button onClick={resetPw} data-testid="reset-pw-submit">Reset Password</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
